<template>
  <b-modal
    v-model="show"
    centered
    scrollable
    size="lg"
    hide-footer
    no-close-on-backdrop
    :hide-header-close="submitting"
    :title="dialogTitle"
    content-class="pos-id-dialog"
    @hidden="onHidden"
  >
    <!-- 步驟 1：填表 -->
    <div v-if="step === 'form'">
      <b-alert v-if="errorTip" show variant="danger">
        <p class="mb-1">{{ errorTip }}</p>
        <b-button
          v-if="lastErrorCode === 10060"
          size="sm"
          variant="outline-danger"
          @click="emitGotoExact"
          >回列表用「精確」模式查詢</b-button
        >
      </b-alert>

      <b-row>
        <b-col md="6" class="mb-1">
          <label class="mb-0">
            院線 circuit_id <span class="text-danger">*</span>
          </label>
          <el-select
            v-model="form.circuit_id"
            size="small"
            filterable
            allow-create
            default-first-option
            clearable
            placeholder="例：MCL"
            style="width: 100%"
          >
            <el-option
              v-for="item in circuitOptionList"
              :key="item.value"
              :label="item.text"
              :value="item.value"
            />
          </el-select>
        </b-col>
        <b-col md="6" class="mb-1">
          <label class="mb-0">pos_id <span class="text-danger">*</span></label>
          <b-form-input
            v-model="form.pos_id"
            size="sm"
            placeholder="例：13527"
          />
        </b-col>
      </b-row>

      <div class="border rounded p-2 mb-2">
        <label class="mb-0">影片搜尋（選用後自動帶出 film_id / master / 標題）</label>
        <div class="d-flex mb-1">
          <b-form-input
            v-model="filmKeyword"
            size="sm"
            placeholder="輸入片名關鍵字，例：DDDD"
            @keyup.enter="searchFilm"
          />
          <b-button
            size="sm"
            variant="outline-primary"
            class="ml-1 flex-shrink-0"
            :disabled="searching"
            @click="searchFilm"
            >搜尋影片</b-button
          >
        </div>
        <div v-if="filmResults.length > 0" class="film-result">
          <div v-for="master in filmResults" :key="master.id" class="mb-1">
            <div>
              <strong>{{ master.name_zh }}</strong>
              <small class="text-muted ml-1">{{ master.name_en }}</small>
            </div>
            <div
              v-for="child in master.children ? master.children : []"
              :key="child.id"
              class="ml-2"
            >
              <b-button
                size="sm"
                variant="link"
                class="p-0 align-baseline"
                @click="pickFilm(master, child)"
                >選用</b-button
              >
              <span class="ml-1">
                {{ child.title_name }}（film_id {{ child.id }} / master
                {{ child.master_id }}）
              </span>
            </div>
          </div>
        </div>
        <div v-else-if="searched" class="text-muted">查無符合的影片</div>
      </div>

      <b-row>
        <b-col md="3" class="mb-1">
          <label class="mb-0">影片 id (film_id) <span class="text-danger">*</span></label>
          <b-form-input v-model="form.film_id" size="sm" placeholder="例：5013" />
        </b-col>
        <b-col md="3" class="mb-1">
          <label class="mb-0">master id（留空由後端帶出）</label>
          <b-form-input v-model="form.film_master_id" size="sm" placeholder="例：3858" />
        </b-col>
        <b-col md="6" class="mb-1">
          <label class="mb-0">標題（留空由後端帶出影片標題）</label>
          <b-form-input v-model="form.title_name" size="sm" />
        </b-col>
      </b-row>

      <b-form-group label="備註（建議填活動名稱）" class="mb-2">
        <b-form-input
          v-model="form.remarks"
          size="sm"
          placeholder="例：DDDD 馬拉松後章"
        />
      </b-form-group>

      <b-form-checkbox v-model="form.backfill_ledger">
        補建匹配記錄：該 (院線 / pos-id / 影片) 三元組在匹配記錄沒有行時，補建一行
      </b-form-checkbox>

      <b-form-group label="緣由（寫入變更記錄，建議填寫）" class="mt-2 mb-0">
        <b-form-input
          v-model="form.reason"
          size="sm"
          placeholder="例：活動登記"
        />
      </b-form-group>

      <div class="float-right mt-3">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mr-1"
          :disabled="previewing"
          @click="close"
          >取消</b-button
        >
        <b-button
          size="sm"
          variant="primary"
          :disabled="previewing"
          @click="preview"
          >預覽</b-button
        >
      </div>
      <div class="clearfix"></div>
    </div>

    <!-- 步驟 2：dry_run 預覽 -->
    <div v-else-if="step === 'preview'">
      <b-alert show variant="info">
        以下為預覽結果，尚未寫入任何資料。
      </b-alert>
      <ul class="mb-2">
        <li>
          院線 / pos-id / 影片：{{ previewSpecial.circuit_id }} /
          {{ previewSpecial.pos_id }} / {{ previewSpecial.film_id }}
        </li>
        <li>匹配記錄：{{ previewLedgerText }}</li>
        <li v-if="previewReEnabled">
          該白名單此前已停用：正式提交會原地重新啟用，不會新增行。
        </li>
        <li>{{ previewAuditText }}</li>
      </ul>

      <div v-if="previewNotes.length > 0">
        <p class="mb-1">notes：</p>
        <ul class="mb-2">
          <li v-for="(note, index) in previewNotes" :key="index">{{ note }}</li>
        </ul>
      </div>
      <p v-else class="text-muted">後端未回傳 notes。</p>

      <div class="float-right mt-2">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mr-1"
          :disabled="submitting"
          @click="backToForm"
          >返回修改</b-button
        >
        <b-button
          size="sm"
          variant="primary"
          :disabled="submitting"
          @click="submit"
          >確認送出</b-button
        >
      </div>
      <div class="clearfix"></div>
    </div>

    <!-- 步驟 3：正式提交結果 -->
    <div v-else>
      <b-alert show variant="success">白名單已處理完成。</b-alert>
      <ul class="mb-2">
        <li v-if="resultReEnabled">
          該白名單此前已停用，本次已原地重新啟用（未新增行）。
        </li>
        <li>匹配記錄：{{ resultLedgerText }}</li>
        <li>{{ resultAuditText }}</li>
      </ul>

      <div v-if="resultNotes.length > 0">
        <p class="mb-1">notes：</p>
        <ul class="mb-2">
          <li v-for="(note, index) in resultNotes" :key="index">{{ note }}</li>
        </ul>
      </div>
      <p v-else class="text-muted">後端未回傳 notes。</p>

      <div class="float-right mt-2">
        <b-button size="sm" variant="primary" @click="close">關閉</b-button>
      </div>
      <div class="clearfix"></div>
    </div>
  </b-modal>
</template>

<script>
import { createFilmCircuitSpecialId } from '@/api/posId'
import { getFilmRunListOfFilm } from '@/api/film-material-management/film'
import {
  buildSpecialIdCreatePayload,
  normalizeNotes,
  resolvePosIdError
} from '@/utils/posId'

function toTrimmedText(value) {
  if (value === undefined || value === null) {
    return ''
  }
  return String(value).trim()
}

// 院線選項新契約：物件陣列 [{ value, text }]（text 建議 'KEY · full_name'）。
// 為相容舊呼叫端，仍接受純字串陣列，此時字串同時作為 value 與 text。
function normalizeCircuitOptions(options) {
  const list = Array.isArray(options) ? options : []
  const normalized = []
  list.forEach((option) => {
    if (option === undefined || option === null) {
      return
    }
    if (typeof option === 'object') {
      const value = toTrimmedText(option.value)
      if (value === '') {
        return
      }
      const text = toTrimmedText(option.text)
      normalized.push({ value, text: text === '' ? value : text })
      return
    }
    const value = toTrimmedText(option)
    if (value === '') {
      return
    }
    normalized.push({ value, text: value })
  })
  return normalized
}

export default {
  name: 'SpecialIdCreateDialog',
  props: {
    // [{ value, text }]（舊格式：純字串陣列亦可，會由 normalizeCircuitOptions 轉換）
    circuitOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      show: false,
      step: 'form',
      searching: false,
      searched: false,
      previewing: false,
      submitting: false,
      filmKeyword: '',
      filmResults: [],
      previewData: null,
      result: null,
      errorTip: '',
      lastErrorCode: null,
      form: {
        circuit_id: '',
        pos_id: '',
        film_id: '',
        film_master_id: '',
        title_name: '',
        remarks: '',
        backfill_ledger: true,
        reason: ''
      }
    }
  },
  computed: {
    circuitOptionList() {
      return normalizeCircuitOptions(this.circuitOptions)
    },
    dialogTitle() {
      if (this.step === 'preview') {
        return '新增白名單 · 預覽'
      }
      if (this.step === 'done') {
        return '新增白名單 · 完成'
      }
      return '新增白名單'
    },
    previewSpecial() {
      if (this.previewData && this.previewData.special) {
        return this.previewData.special
      }
      return {}
    },
    previewNotes() {
      return normalizeNotes(this.previewData ? this.previewData.notes : [])
    },
    previewLedgerText() {
      if (!this.previewData) {
        return '—'
      }
      const ledger = this.previewData.ledger ? this.previewData.ledger : {}
      if (ledger.created === true) {
        return '將補建一行匹配記錄（預覽，尚未寫入）'
      }
      return '不補建（該三元組已有匹配記錄，或未勾選補建匹配記錄）'
    },
    previewReEnabled() {
      return Boolean(this.previewData && this.previewData.re_enabled === true)
    },
    previewAuditText() {
      const auditId = this.previewData ? this.previewData.audit_id : null
      if (auditId === null || auditId === undefined) {
        return '預覽未寫入變更記錄（audit_id = null）。'
      }
      return `預覽的變更記錄 id：${auditId}（預覽不會真的寫入）。`
    },
    resultNotes() {
      return normalizeNotes(this.result ? this.result.notes : [])
    },
    resultReEnabled() {
      return Boolean(this.result && this.result.re_enabled === true)
    },
    resultLedgerText() {
      if (!this.result) {
        return '—'
      }
      const ledger = this.result.ledger ? this.result.ledger : {}
      if (ledger.created !== true) {
        return '未補建匹配記錄（該三元組已有匹配記錄，或未勾選補建匹配記錄）'
      }
      if (ledger.id === null || ledger.id === undefined || ledger.id === '') {
        return '已補建匹配記錄'
      }
      return `已補建匹配記錄 #${ledger.id}`
    },
    resultAuditText() {
      const auditId = this.result ? this.result.audit_id : null
      if (auditId === null || auditId === undefined || auditId === '') {
        return '變更記錄 id：（後端未回傳）'
      }
      return `變更記錄 id：${auditId}`
    }
  },
  methods: {
    open() {
      this.resetAll()
      this.show = true
    },
    close() {
      if (this.submitting) {
        return
      }
      this.show = false
    },
    onHidden() {
      this.resetAll()
    },
    resetAll() {
      this.step = 'form'
      this.searching = false
      this.searched = false
      this.previewing = false
      this.submitting = false
      this.filmKeyword = ''
      this.filmResults = []
      this.previewData = null
      this.result = null
      this.errorTip = ''
      this.lastErrorCode = null
      this.form = {
        circuit_id: '',
        pos_id: '',
        film_id: '',
        film_master_id: '',
        title_name: '',
        remarks: '',
        backfill_ledger: true,
        reason: ''
      }
    },
    searchFilm() {
      const keyword = String(this.filmKeyword).trim()
      if (keyword === '') {
        this.$message.warning('請先輸入片名關鍵字')
        return
      }
      this.searching = true
      this.searched = false
      getFilmRunListOfFilm({ search: keyword })
        .then((res) => {
          const list = res && Array.isArray(res.data) ? res.data : []
          this.filmResults = list
          this.searched = true
        })
        .catch((error) => {
          this.filmResults = []
          this.searched = true
          this.$message.error(resolvePosIdError(error).message)
        })
        .finally(() => {
          this.searching = false
        })
    },
    pickFilm(master, child) {
      if (!child) {
        return
      }
      this.form.film_id = child.id === null || child.id === undefined ? '' : String(child.id)
      let masterId = child.master_id
      if (masterId === null || masterId === undefined || masterId === '') {
        masterId = master ? master.id : ''
      }
      this.form.film_master_id =
        masterId === null || masterId === undefined ? '' : String(masterId)
      this.form.title_name = child.title_name ? String(child.title_name) : ''
      this.$message.success('已帶入影片資料，可再自行修改')
    },
    validate() {
      if (String(this.form.circuit_id).trim() === '') {
        this.$message.warning('請填院線 circuit_id')
        return false
      }
      if (String(this.form.pos_id).trim() === '') {
        this.$message.warning('請填 pos_id')
        return false
      }
      if (String(this.form.film_id).trim() === '') {
        this.$message.warning('請填影片 id film_id（可用上方影片搜尋帶出）')
        return false
      }
      return true
    },
    preview() {
      if (this.previewing || !this.validate()) {
        return
      }
      this.errorTip = ''
      this.lastErrorCode = null
      this.previewing = true
      // 先 dry_run=1 預覽：不寫庫、不寫審計
      createFilmCircuitSpecialId(buildSpecialIdCreatePayload(this.form, true))
        .then((res) => {
          this.previewData = res && res.data ? res.data : {}
          this.step = 'preview'
        })
        .catch((error) => {
          this.handleError(error)
        })
        .finally(() => {
          this.previewing = false
        })
    },
    backToForm() {
      this.step = 'form'
      this.errorTip = ''
      this.lastErrorCode = null
    },
    submit() {
      if (this.submitting) {
        return
      }
      this.submitting = true
      createFilmCircuitSpecialId(buildSpecialIdCreatePayload(this.form, false))
        .then((res) => {
          this.result = res && res.data ? res.data : {}
          this.step = 'done'
          if (this.result.re_enabled === true) {
            this.$message.success(
              '該白名單此前已停用，本次已原地重新啟用（未新增行）'
            )
          } else {
            this.$message.success('白名單已登記')
          }
          const ledger = this.result.ledger ? this.result.ledger : {}
          if (ledger.created === true) {
            if (ledger.id === null || ledger.id === undefined) {
              this.$message.success('已補建匹配記錄')
            } else {
              this.$message.success(`已補建匹配記錄 #${ledger.id}`)
            }
          }
          this.$emit('created')
        })
        .catch((error) => {
          this.handleError(error)
        })
        .finally(() => {
          this.submitting = false
        })
    },
    handleError(error) {
      const info = resolvePosIdError(error)
      if (info.isPermissionDenied) {
        this.$emit('permission-denied')
        this.show = false
        return info
      }
      this.lastErrorCode = info.code
      this.step = 'form'
      if (info.code === 10060) {
        this.errorTip =
          '該 pos-id 已授權該影片（同一院線 / pos-id / 影片的有效白名單已存在）。請回列表用「精確」模式查詢該筆記錄。'
      } else if (info.code === 10061) {
        this.errorTip =
          '影片與 master 不符：請把 film_master_id 留空由後端自動帶出，或改成該影片正確的 master。'
      } else if (info.code === 10010) {
        this.errorTip = `${info.message} 請重新選擇院線 / 影片後再試。`
      } else {
        this.errorTip = info.message
      }
      this.$message.error(this.errorTip)
      return info
    },
    emitGotoExact() {
      this.$emit('goto-exact', {
        circuit_id: this.form.circuit_id,
        pos_id: this.form.pos_id
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.film-result {
  max-height: 220px;
  overflow-y: auto;
}
</style>
